import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  Tabs, 
  Descriptions, 
  Table, 
  message,
  Modal,
  Form,
  Input,
  Alert,
  Progress,
  Spin,
  Image
} from 'antd';
import {
  PlayCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  CrownOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import ConfigManagement from './ConfigManagement';
import ModManagement from './ModManagement';
import ServerMonitoring from './ServerMonitoring';
import ARKConfigManager from './ARKConfigManager';
import SaveManagement from './SaveManagement';
import PluginManagement from '../../components/PluginManagement';
import ShopManagement from './ShopManagement';
import ARKRCONConsole from './ARKRCONConsole';


interface Server {
  id: number;
  name: string;
  game_name: string;
  game_icon?: string;
  status: string;
  port: number;
  max_players: number;
  expires_at: string;
  created_at: string;
  node_name: string;
  node_host: string;
  supports_mods: boolean;
  is_steam_game: boolean;
  mod_support_enabled: number;
  is_activation_code: number;
  taobao_receipt_verified: number;
  deletion_scheduled_at?: string;
  config: any;
  server_billing_period?: string;
}


interface ServerLog {
  id: number;
  action: string;
  details: string;
  username: string;
  created_at: string;
}

const ServerDetail: React.FC = () => {
  const { serverId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [server, setServer] = useState<Server | null>(null);
  const [logs, setLogs] = useState<ServerLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [renewModalVisible, setRenewModalVisible] = useState(false);
  const [installProgressModalVisible, setInstallProgressModalVisible] = useState(false);
  const [installProgress, setInstallProgress] = useState<any>(null);
  
  // 启动进度相关状态
  const [startupProgress, setStartupProgress] = useState(0);
  const [startupStatus, setStartupStatus] = useState<'waiting' | 'monitoring' | 'completed' | 'error' | 'timeout'>('waiting');
  const [startupMessage, setStartupMessage] = useState('');
  const [startupVisible, setStartupVisible] = useState(false);
  const [memoryUsage, setMemoryUsage] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareOnlineCount, setShareOnlineCount] = useState<number | null>(null);
  const [shareOnlineCountLoading, setShareOnlineCountLoading] = useState(false);
  const [renewalInfo, setRenewalInfo] = useState<{
    gameTypeId: number;
    maxPlayers: number;
    pricingOptions: Array<{
      billingPeriod: string;
      price: number;
      durationDays: number;
    }>;
  } | null>(null);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState<string>('');
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponValidation, setCouponValidation] = useState<any>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  
  // 淘宝收货码验证相关状态
  const [taobaoVerificationEnabled, setTaobaoVerificationEnabled] = useState(false);
  const [receiptCodeModalVisible, setReceiptCodeModalVisible] = useState(false);
  const [receiptCodeInput, setReceiptCodeInput] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [forceShowVerification, setForceShowVerification] = useState(false);
  const [goodReviewModalVisible, setGoodReviewModalVisible] = useState(false);
  const [bonusDays, setBonusDays] = useState(0);
  const [reviewBonusDays, setReviewBonusDays] = useState(0);

  const loadServerDetail = useCallback(async () => {
    try {
      console.log('🔄 加载服务器详情:', serverId);
      const response = await api.get(`/servers/${serverId}`);
      console.log('✅ 服务器详情数据:', response.data);
      setServer(response.data);
    } catch (error: any) {
      console.error('❌ 加载服务器详情失败:', error);
      message.error(error.response?.data?.message || '加载服务器详情失败');
      navigate('/servers');
    } finally {
      setLoading(false);
    }
  }, [serverId, navigate]);

  const loadServerLogs = useCallback(async () => {
    try {
      const response = await api.get(`/servers/${serverId}/logs?limit=20`);
      setLogs(response.data);
    } catch (error) {
      console.error('加载日志失败:', error);
    }
  }, [serverId]);

  const loadRenewalInfo = useCallback(async () => {
    try {
      const response = await api.get(`/servers/${serverId}/renewal-info`);
      setRenewalInfo(response.data);
      // 默认选择第一个付款周期
      if (response.data.pricingOptions && response.data.pricingOptions.length > 0) {
        setSelectedBillingPeriod(response.data.pricingOptions[0].billingPeriod);
      }
    } catch (error) {
      console.error('加载续费信息失败:', error);
    }
  }, [serverId]);

  // 加载淘宝收货码验证配置
  const loadTaobaoVerificationConfig = useCallback(async () => {
    try {
      const response = await api.get('/admin/system/public-config');
      setTaobaoVerificationEnabled(response.data.taobao_verification_enabled === true);
    } catch (error) {
      console.error('加载淘宝验证配置失败:', error);
    }
  }, []);



  // 验证淘宝收货码（需要服务器ID）
  const verifyReceiptCode = useCallback(async (code: string) => {
    if (!code.trim() || !serverId) return;
    
    setVerifyingCode(true);
    try {
      const response = await api.post('/taobao-receipt-codes/verify', {
        code: code.trim(),
        server_id: serverId
      });
      
      if (response.data.success) {
        message.success('收货码验证成功！功能已激活！');
        setReceiptCodeModalVisible(false);
        setReceiptCodeInput('');
        setForceShowVerification(false);
        // 重新加载服务器信息以获取最新的验证状态
        const serverResponse = await api.get(`/servers/${serverId}`);
        const updatedServer = serverResponse.data;
        setServer(updatedServer);
        
        // 显示好评赠时长弹窗
        console.log('准备显示好评赠时长弹窗，服务器信息:', updatedServer);
        if (updatedServer) {
          let days = 0;
          let reviewBonusDays = 0;
          
          switch (updatedServer.server_billing_period) {
            case 'weekly':
              days = 1;
              reviewBonusDays = 2;
              break;
            case 'monthly':
              days = 2;
              reviewBonusDays = 5;
              break;
            case 'quarterly':
              days = 7;
              reviewBonusDays = 21;
              break;
            case 'semiannual':
              days = 20;
              reviewBonusDays = 40;
              break;
            case 'yearly':
              days = 30;
              reviewBonusDays = 90;
              break;
            default:
              days = 2; // 默认月付标准
              reviewBonusDays = 5;
          }
          
          console.log('设置弹窗状态 - days:', days, 'reviewBonusDays:', reviewBonusDays);
          setBonusDays(days);
          setReviewBonusDays(reviewBonusDays);
          setGoodReviewModalVisible(true);
          console.log('弹窗状态已设置为可见');
        } else {
          console.log('服务器信息为空，无法显示弹窗');
        }
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '收货码验证失败');
    } finally {
      setVerifyingCode(false);
    }
  }, [serverId, loadServerDetail]);

  const validateCoupon = useCallback(async (code: string) => {
    if (!code || !selectedBillingPeriod || !renewalInfo) return;
    
    setCouponLoading(true);
    try {
      const response = await api.post('/admin/coupons/validate', {
        code,
        game_type_id: renewalInfo.gameTypeId,
        billing_period: selectedBillingPeriod
      });
      setCouponValidation(response.data.coupon);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '优惠码验证失败';
      setCouponValidation({ error: errorMessage });
      // 不显示额外的错误提示，因为UI中已经显示了错误信息
    } finally {
      setCouponLoading(false);
    }
  }, [selectedBillingPeriod, renewalInfo]);

  useEffect(() => {
    if (serverId) {
      loadServerDetail();
      loadServerLogs();
      loadRenewalInfo();
      loadTaobaoVerificationConfig();
    }
  }, [serverId, loadServerDetail, loadServerLogs, loadRenewalInfo, loadTaobaoVerificationConfig]);

  // 处理URL参数中的tab参数
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // 监听全局验证事件
  useEffect(() => {
    const handleVerificationRequired = (event: CustomEvent) => {
      const { serverId: eventServerId } = event.detail;
      if (eventServerId && serverId && parseInt(eventServerId) === parseInt(serverId)) {
        setForceShowVerification(true);
      }
    };

    window.addEventListener('taobaoVerificationRequired', handleVerificationRequired as EventListener);
    
    return () => {
      window.removeEventListener('taobaoVerificationRequired', handleVerificationRequired as EventListener);
    };
  }, [serverId]);


  // Socket.IO监听启动进度
  useEffect(() => {
    if (!serverId) return;

    console.log(`[前端] 开始初始化Socket.IO监听`);
    console.log(`[前端] window.io:`, (window as any).io);
    console.log(`[前端] serverId:`, serverId);
    
    const socket = (window as any).io;
    let joinRoom: (() => void) | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;
    
    if (socket && serverId) {
      console.log(`[前端] Socket.IO对象存在，连接状态:`, socket.connected);
      
      // 等待Socket.IO连接建立
      joinRoom = () => {
        try {
        console.log(`[前端] 准备加入服务器 ${serverId} 房间`);
        socket.emit('join-server-room', parseInt(serverId));
        console.log(`[前端] 已发送加入服务器 ${serverId} 房间的请求`);
        } catch (error) {
          console.error(`[前端] 加入房间失败:`, error);
        }
      };
      
      // 重连处理函数
      const handleReconnect = () => {
        console.log(`[前端] Socket.IO重连，重新加入房间`);
        if (joinRoom) {
          joinRoom();
        }
      };
      
      if (socket.connected) {
        console.log(`[前端] Socket.IO已连接，立即加入房间`);
        joinRoom();
      } else {
        console.log(`[前端] Socket.IO未连接，等待连接事件`);
        socket.on('connect', () => {
          console.log(`[前端] Socket.IO连接建立，加入房间`);
          joinRoom?.();
        });
      }
      
      // 监听重连事件
      socket.on('reconnect', handleReconnect);
      
      // 添加连接错误处理
      socket.on('connect_error', (error: any) => {
        console.error(`[前端] Socket.IO连接错误:`, error);
        // 设置重连超时
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
        reconnectTimeout = setTimeout(() => {
          console.log(`[前端] 尝试重新连接Socket.IO`);
          if (socket.disconnected) {
            socket.connect();
          }
        }, 5000);
      });
      
      // 添加断开连接处理
      socket.on('disconnect', (reason: any) => {
        console.log(`[前端] Socket.IO断开连接:`, reason);
        // 如果是意外断开，尝试重连
        if (reason === 'io server disconnect') {
          console.log(`[前端] 服务器主动断开，尝试重连`);
          setTimeout(() => {
            if (socket.disconnected) {
              socket.connect();
            }
          }, 2000);
        }
      });
      
      const handleStartupProgress = (data: any) => {
        try {
        console.log(`[前端] 收到启动进度事件:`, data);
        if (data.serverId === serverId || data.serverId === parseInt(serverId)) {
          console.log(`[前端] 更新进度: ${data.progress}%, 状态: ${data.status}, 消息: ${data.message}`);
          setStartupProgress(data.progress);
          setStartupStatus(data.status);
          setStartupMessage(data.message);
          if (data.memoryUsage) setMemoryUsage(data.memoryUsage);
          
          // 如果启动完成，关闭弹窗
          if (data.status === 'completed') {
            setTimeout(() => {
              setStartupVisible(false);
              loadServerDetail(); // 刷新服务器状态
            }, 2000);
          }
          }
        } catch (error) {
          console.error(`[前端] 处理启动进度事件失败:`, error);
        }
      };
      
      const handleServerStatus = (data: any) => {
        try {
        console.log(`[前端] 收到服务器状态事件:`, data);
        if (data.serverId === serverId || data.serverId === parseInt(serverId)) {
          // 只更新状态，保留其他服务器信息
          setServer(prevServer => {
            if (prevServer) {
              return {
                ...prevServer,
                status: data.status || prevServer.status
              };
            }
            return prevServer;
          });
          }
        } catch (error) {
          console.error(`[前端] 处理服务器状态事件失败:`, error);
        }
      };
      
      socket.on('server-startup-progress', handleStartupProgress);
      socket.on('server-status', handleServerStatus);
      
      return () => {
        // 清理事件监听器
        socket.off('server-startup-progress', handleStartupProgress);
        socket.off('server-status', handleServerStatus);
          socket.off('connect', joinRoom);
        socket.off('reconnect', handleReconnect);
        
        // 清理重连超时
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
      };
    } else {
      console.log(`[前端] Socket.IO对象不存在或serverId为空`);
      console.log(`[前端] socket:`, socket);
      console.log(`[前端] serverId:`, serverId);
    }
  }, [serverId, loadServerDetail]);

  const handleServerAction = async (action: string) => {
    setActionLoading(action);
    
    try {
      if (action === 'start') {
        await api.post(`/server-management/${serverId}/start`);
        // 显示启动进度弹窗
        setStartupVisible(true);
        setStartupProgress(0);
        setStartupStatus('waiting');
        setStartupMessage('正在启动服务器...');
        setMemoryUsage(0); // 重置内存使用数据
      } else if (action === 'stop') {
        await api.post(`/server-management/${serverId}/stop`);
        // 关闭启动进度弹窗
        setStartupVisible(false);
        setStartupProgress(0);
        setStartupStatus('waiting');
        setStartupMessage('');
      } else if (action === 'restart') {
        await api.post(`/server-management/${serverId}/restart`);
      } else {
        await api.post(`/servers/${serverId}/${action}`);
      }
      message.success(`服务器${action === 'start' ? '启动' : action === 'stop' ? '停止' : '重启'}指令已发送`);
      
      // 更新服务器状态
      if (server) {
        setServer({
          ...server,
          status: action === 'start' ? 'starting' : action === 'stop' ? 'stopped' : 'restarting'
        });
      }
      
      // 重新加载日志
      setTimeout(() => {
        loadServerLogs();
      }, 1000);
    } catch (error: any) {
      message.error(error.response?.data?.message || '操作失败');
    } finally {
      setActionLoading('');
    }
  };

  const handleRenew = async (values: any) => {
    try {
      const response = await api.post(`/servers/${serverId}/renew`, {
        billing_period: selectedBillingPeriod,
        coupon_code: couponCode || undefined
      });
      
      message.success(`续费成功！实付金额：¥${response.data.finalPrice}`);
      setRenewModalVisible(false);
      setCouponCode('');
      setCouponValidation(null);
      loadServerDetail();
    } catch (error: any) {
      message.error(error.response?.data?.message || '续费失败');
    }
  };

  const getShareOnlineCount = async () => {
    if (!serverId || !server || server.status !== 'running') {
      setShareOnlineCount(null);
      return;
    }

    setShareOnlineCountLoading(true);
    try {
      console.log('🔄 分享模态框查询在线人数:', serverId);
      const response = await api.get(`/ark-rcon/servers/${serverId}/players`);
      console.log('✅ 分享模态框在线人数数据:', response.data);
      
      // 检查响应数据格式
      if (response.data && response.data.success && Array.isArray(response.data.players)) {
        setShareOnlineCount(response.data.players.length);
      } else if (response.data && Array.isArray(response.data)) {
        // 兼容旧格式
        setShareOnlineCount(response.data.length);
      } else {
        setShareOnlineCount(0);
      }
    } catch (error: any) {
      console.error('❌ 分享模态框获取在线人数失败:', error);
      setShareOnlineCount(null);
    } finally {
      setShareOnlineCountLoading(false);
    }
  };

  const handleShareServer = () => {
    setShareModalVisible(true);
    // 打开模态框时获取在线人数
    if (server && server.status === 'running') {
      getShareOnlineCount();
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/share/server/${serverId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      message.success('分享链接已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败，请手动复制');
    });
  };

  const getSteamConnectLink = () => {
    if (!server) return '';
    return `steam://connect/${server.node_host}:${server.port}`;
  };

  const getBillingPeriodName = (period: string) => {
    const periodMap: { [key: string]: string } = {
      'weekly': '周付',
      'monthly': '月付',
      'quarterly': '季付',
      'semiannual': '半年付',
      'yearly': '年付'
    };
    return periodMap[period] || period;
  };

  const getCurrentPrice = () => {
    if (!renewalInfo || !selectedBillingPeriod) return 0;
    const option = renewalInfo.pricingOptions.find(p => p.billingPeriod === selectedBillingPeriod);
    return option ? Number(option.price) || 0 : 0;
  };

  const getFinalPrice = () => {
    const basePrice = getCurrentPrice();
    if (!couponValidation || couponValidation.error) return basePrice || 0;
    
    const discountValue = Number(couponValidation.discount_value) || 0;
    const discountAmount = couponValidation.discount_type === 'percentage' ? 
      (basePrice || 0) * (discountValue / 100) : 
      discountValue;
    
    return Math.max(0, (basePrice || 0) - discountAmount);
  };

  // 获取安装进度
  const fetchInstallProgress = async () => {
    try {
      const response = await api.get(`/servers/${serverId}/install-progress`);
      setInstallProgress(response.data);
    } catch (error) {
      console.error('获取安装进度失败:', error);
      message.error('获取安装进度失败');
    }
  };

  // 查看安装进度
  const viewInstallProgress = () => {
    fetchInstallProgress();
    setInstallProgressModalVisible(true);
  };

  // 取消安装
  const cancelInstall = async () => {
    try {
      await api.delete(`/servers/${serverId}`);
      message.success('安装已取消');
      setInstallProgressModalVisible(false);
      // 刷新服务器状态
      loadServerDetail();
    } catch (error: any) {
      message.error(error.response?.data?.message || '取消安装失败');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      running: { color: 'green', text: '运行中' },
      stopped: { color: 'red', text: '已停止' },
      starting: { color: 'orange', text: '启动中' },
      restarting: { color: 'orange', text: '重启中' },
      installing: { color: 'blue', text: '安装中' },
      error: { color: 'red', text: '错误' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const getActionText = (action: string) => {
    const actionMap: { [key: string]: string } = {
      created: '创建',
      started: '启动',
      stopped: '停止',
      restarted: '重启',
      config_updated: '配置更新',
      mod_installed: 'MOD安装',
      mod_enabled: 'MOD启用',
      mod_disabled: 'MOD禁用',
      mod_uninstalled: 'MOD卸载',
      renewed: '续费',
      expired: '过期',
      force_stopped: '强制停止'
    };
    return actionMap[action] || action;
  };

  const logColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (action: string) => getActionText(action),
    },
    {
      title: '详情',
      dataIndex: 'details',
      key: 'details',
    },
    {
      title: '操作用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (username: string) => username || '系统',
    },
  ];

  if (loading || !server) {
    return <div>加载中...</div>;
  }

  const isExpired = new Date(server.expires_at) < new Date();
  
  // 判断是否需要显示淘宝收货码验证限制
  const needsTaobaoVerification = () => {
    return forceShowVerification || (
      taobaoVerificationEnabled && 
      server.is_activation_code === 1 && 
      server.taobao_receipt_verified !== 1
    );
  };

  return (
    <div className="fade-in">
      <div className="mb-6">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/servers')}
          className="mb-4"
        >
          返回服务器列表
        </Button>
        
        {/* 桌面端布局 */}
        <div className="hidden md:flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-3">
              <Image
                src={server.game_icon || '/images/game-icons/placeholder_184x69.jpg'}
                alt={server.game_name}
                width={120}
                height={45}
                style={{ objectFit: 'cover', borderRadius: '6px' }}
                preview={false}
                fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTAwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjUwIiB5PSIyMCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+R2FtZTwvdGV4dD4KPC9zdmc+"
                onError={(e) => {
                  console.error('图标加载失败:', server.game_icon);
                }}
                onLoad={() => {
                  console.log('图标加载成功:', server.game_icon);
                }}
              />
              <div>
                <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-bold">{server.name}</h2>
                  {server.is_activation_code === 1 && (
                    <Tag color="blue" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                      激活码
                    </Tag>
                  )}
                </div>
            <p className="text-gray-600">{server.game_name} 服务器</p>
              </div>
              <Button
                type="primary"
                icon={<ShareAltOutlined />}
                onClick={handleShareServer}
                style={{
                  backgroundColor: '#13c2c2',
                  borderColor: '#13c2c2',
                  height: '32px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(19, 194, 194, 0.3)',
                  transition: 'all 0.3s ease',
                  marginLeft: '20px'
                }}
              >
                分享游戏
              </Button>
            </div>
          </div>
          
          <div style={{ marginTop: '20px' }}>
          <Space>
            {server.status === 'stopped' && !isExpired && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={actionLoading === 'start'}
                onClick={() => handleServerAction('start')}
                style={{
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                  height: '40px',
                  minWidth: '120px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(82, 196, 26, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                启动服务器
              </Button>
            )}
            
            {(server.status === 'running' || server.status === 'starting') && (
              <Button
                type="primary"
                danger
                icon={<StopOutlined />}
                loading={actionLoading === 'stop'}
                onClick={() => handleServerAction('stop')}
                style={{
                  height: '40px',
                  minWidth: '100px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                停止服务器
              </Button>
            )}
            
            {server.status === 'running' && (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={actionLoading === 'restart'}
                onClick={() => handleServerAction('restart')}
                style={{
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff',
                  height: '40px',
                  minWidth: '100px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                重启服务器
              </Button>
            )}
            
            <Button
              type="primary"
              icon={<CrownOutlined />}
              onClick={() => setRenewModalVisible(true)}
              style={{
                backgroundColor: '#722ed1',
                borderColor: '#722ed1',
                height: '40px',
                minWidth: '80px',
                fontWeight: 'bold',
                fontSize: '14px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(114, 46, 209, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              续费
            </Button>
          </Space>
          </div>
        </div>

        {/* 移动端布局 */}
        <div className="md:hidden">
          {/* 服务器信息卡片 */}
          <Card className="mb-4">
            <div className="text-center py-4">
              <div className="flex items-center justify-center space-x-3">
                <Image
                  src={server.game_icon || '/images/game-icons/placeholder_184x69.jpg'}
                  alt={server.game_name}
                  width={100}
                  height={40}
                  style={{ objectFit: 'cover', borderRadius: '6px' }}
                  preview={false}
                  fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCA4MCAzMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjMwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjQwIiB5PSIxNSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+R2FtZTwvdGV4dD4KPC9zdmc+"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-bold">{server.name}</h2>
                    {server.is_activation_code === 1 && (
                      <Tag color="blue" style={{ fontSize: '10px', fontWeight: 'bold' }}>
                        激活码
                      </Tag>
                    )}
                    <Button
                      type="primary"
                      icon={<ShareAltOutlined />}
                      onClick={handleShareServer}
                      size="small"
                      style={{
                        backgroundColor: '#13c2c2',
                        borderColor: '#13c2c2',
                        height: '32px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(19, 194, 194, 0.3)',
                        transition: 'all 0.3s ease',
                        minWidth: '60px',
                        padding: '4px 8px'
                      }}
                    >
                      分享
                    </Button>
                  </div>
                  <p className="text-gray-600 text-sm">{server.game_name} 服务器</p>
                </div>
              </div>
            </div>
          </Card>

          {/* 操作按钮 */}
          <div className="grid grid-cols-3 gap-2 mb-4" style={{ marginTop: '20px' }}>
            {server.status === 'stopped' && !isExpired && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={actionLoading === 'start'}
                onClick={() => handleServerAction('start')}
                size="large"
                block
                style={{
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                  height: '40px',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(82, 196, 26, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                启动
              </Button>
            )}
            
            {(server.status === 'running' || server.status === 'starting') && (
              <Button
                type="primary"
                danger
                icon={<StopOutlined />}
                loading={actionLoading === 'stop'}
                onClick={() => handleServerAction('stop')}
                size="large"
                block
                style={{
                  height: '40px',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                停止
              </Button>
            )}
            
            {server.status === 'running' && (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={actionLoading === 'restart'}
                onClick={() => handleServerAction('restart')}
                size="large"
                block
                style={{
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff',
                  height: '40px',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                重启
              </Button>
            )}
            
            <Button
              type="primary"
              icon={<CrownOutlined />}
              onClick={() => setRenewModalVisible(true)}
              size="large"
              block
              style={{
                backgroundColor: '#722ed1',
                borderColor: '#722ed1',
                height: '40px',
                fontWeight: 'bold',
                fontSize: '12px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(114, 46, 209, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              续费
            </Button>
          </div>
        </div>
      </div>

      {isExpired && (
        <Alert
          message="服务器已过期"
          description={`您的服务器已过期，请及时续费以免数据丢失，过期7天后服务器将被自动删除。${server.deletion_scheduled_at ? `当前服务器删除时间：${new Date(server.deletion_scheduled_at).toLocaleString()}` : '删除时间待定'}`}
          type="error"
          showIcon
          className="mb-6"
        />
      )}

      <div style={{ position: 'relative' }}>
        {/* 淘宝收货码验证限制遮罩 */}
        {needsTaobaoVerification() && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '12px',
              textAlign: 'center',
              maxWidth: '500px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '20px', color: '#1890ff' }}>
                🔒
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#333' }}>
                请输入淘宝收货码启用所有功能
              </h3>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', lineHeight: '1.6' }}>
                未确认收货前仅对服务器进行启动和停止操作，淘宝确认收货后会自动发送发货码给您，在未确认收货前仅提供服务器延迟测试。
              </p>
              <p style={{ fontSize: '14px', color: '#1b950e', marginBottom: '24px', lineHeight: '1.6' }}>
                确认收货后自动赠送时长奖励，感谢您支持悠悠鸟游戏
              </p>
              <Button
                type="primary"
                size="large"
                onClick={() => setReceiptCodeModalVisible(true)}
                style={{
                  height: '40px',
                  minWidth: '120px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                输入收货码
              </Button>
            </div>
          </div>
      )}

      <Tabs 
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
          tabBarStyle={{
            backgroundColor: 'rgba(248, 249, 250, 0.6)',
            borderRadius: '6px',
            padding: '8px',
            marginBottom: '16px'
          }}
        items={[
          {
            key: 'overview',
            label: '概览',
            children: (
              <div className="space-y-4">
                {/* 服务器基本信息 - 移动端优化布局 */}
                <Card 
                  title={
                    <div style={{
                      textAlign: 'center',
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                      marginBottom: '12px'
                    }}>
                      <span style={{ 
                        fontSize: '18px', 
                        fontWeight: 'bold',
                        color: 'var(--text-color)'
                      }}>
                        服务器基本信息
                      </span>
                    </div>
                  }
                  size="default"
                  className="md:hidden"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e8e8e8'
                  }}
                >
                  <div style={{ padding: '8px 0' }}>
                    {/* 服务器信息网格 */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      {/* 服务器状态 */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <span style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary-color)',
                          fontWeight: '600'
                        }}>
                          状态:
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {getStatusTag(server.status)}
                        </div>
                      </div>

                      {/* 游戏类型 */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <span style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary-color)',
                          fontWeight: '600'
                        }}>
                          游戏:
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ 
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'var(--text-color)'
                          }}>
                            {server.game_name}
                          </span>
                          {server.is_steam_game && <Tag color="blue" style={{ fontSize: '10px' }}>Steam</Tag>}
                        </div>
                      </div>
                    </div>

                    {/* 服务器地址 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                      marginBottom: '12px'
                    }}>
                      <span style={{ 
                        fontSize: '12px', 
                        color: 'var(--text-secondary-color)',
                        fontWeight: '600'
                      }}>
                        地址:
                      </span>
                      <span style={{ 
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: 'var(--text-color)',
                        wordBreak: 'break-word',
                        textAlign: 'right',
                        maxWidth: '60%'
                      }}>
                        {server.node_host}:{server.port}
                      </span>
                    </div>

                    {/* 玩家数和节点 */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      {/* 最大玩家数 */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <span style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary-color)',
                          fontWeight: '600'
                        }}>
                          玩家:
                        </span>
                        <span style={{ 
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: 'var(--text-color)'
                        }}>
                          {server.max_players}
                        </span>
                      </div>

                      {/* 服务器节点 */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        <span style={{ 
                          fontSize: '12px', 
                          color: 'var(--text-secondary-color)',
                          fontWeight: '600'
                        }}>
                          节点:
                        </span>
                        <span style={{ 
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: 'var(--text-color)',
                          wordBreak: 'break-word',
                          textAlign: 'right',
                          maxWidth: '60%'
                        }}>
                          {server.node_name}
                        </span>
                      </div>
                    </div>

                    {/* MOD支持 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef',
                      marginBottom: '12px'
                    }}>
                      <span style={{ 
                        fontSize: '12px', 
                        color: 'var(--text-secondary-color)',
                        fontWeight: '600'
                      }}>
                        MOD:
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {server.mod_support_enabled === 1 ? (
                          <Tag color="green" style={{ fontSize: '10px' }}>已开通</Tag>
                        ) : (
                          <Button 
                            type="link" 
                            size="small"
                            onClick={() => setActiveTab('mods')}
                            style={{ padding: '0', height: 'auto' }}
                          >
                            <Tag color="orange" style={{ fontSize: '10px' }}>未开通</Tag>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* 到期时间 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: isExpired ? '#fff2f0' : '#f6ffed',
                      borderRadius: '6px',
                      border: `1px solid ${isExpired ? '#ffccc7' : '#b7eb8f'}`
                    }}>
                      <span style={{ 
                        fontSize: '12px', 
                        color: isExpired ? '#ff4d4f' : '#52c41a',
                        fontWeight: '600'
                      }}>
                        到期:
                      </span>
                      <span style={{ 
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: isExpired ? '#ff4d4f' : '#52c41a'
                      }}>
                        {isExpired ? '已过期' : `${new Date(server.expires_at).toLocaleDateString()} (剩余${Math.ceil((new Date(server.expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))}天)`}
                      </span>
                    </div>
                  </div>
                </Card>

                {/* 桌面端保持原有布局 */}
                <Card 
                  title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>服务器基本信息</span>} 
                  size="small"
                  className="hidden md:block"
                >
                <Descriptions column={2} bordered>
                  <Descriptions.Item label="服务器状态">
                    <Space>
                      {getStatusTag(server.status)}
                      {server.status === 'installing' && (
                        <Button 
                          type="link" 
                          size="small" 
                          icon={<InfoCircleOutlined />}
                          onClick={viewInstallProgress}
                        >
                          查看进度
                        </Button>
                      )}
                      {server.status === 'starting' && (
                        <Button 
                          type="link" 
                          size="small" 
                          icon={<InfoCircleOutlined />}
                          onClick={() => setStartupVisible(true)}
                        >
                          查看启动进度
                        </Button>
                      )}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="游戏类型">
                    {server.game_name}
                    {server.is_steam_game && <Tag color="blue" className="ml-2">Steam</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="服务器地址">
                    {server.node_host}:{server.port}
                  </Descriptions.Item>
                  <Descriptions.Item label="最大玩家数">
                    {server.max_players}
                  </Descriptions.Item>
                  <Descriptions.Item label="服务器节点">
                    {server.node_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="MOD支持">
                      {server.mod_support_enabled === 1 ? (
                        <Tag color="green">已开通MOD功能</Tag>
                      ) : (
                        <Button 
                          type="link" 
                          size="small"
                          onClick={() => setActiveTab('mods')}
                        >
                          <Tag color="orange">未开通</Tag> 点击开通
                        </Button>
                      )}
                  </Descriptions.Item>
                  <Descriptions.Item label="到期时间">
                    <span className={isExpired ? 'text-red-500 font-medium' : ''}>
                      {new Date(server.expires_at).toLocaleString()}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="创建时间">
                    {server.created_at ? new Date(server.created_at).toLocaleString() : '未知'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
              </div>
            )
          },
          {
            key: 'monitoring',
            label: server && server.game_name && server.game_name.toLowerCase().includes('ark') ? 'RCON控制台' : '性能监控',
            children: server && server.game_name && server.game_name.toLowerCase().includes('ark') ? 
              <ARKRCONConsole serverId={serverId!} /> : 
              <ServerMonitoring serverId={serverId!} />
          },
          {
            key: 'config',
            label: '配置管理',
            children: server && server.game_name ? 
              <ConfigManagement serverId={serverId!} gameType={server.game_name} serverInfo={server} onTabChange={setActiveTab} /> :
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>加载服务器信息中...</div>
              </div>
          },
          ...(server && server.game_name && (server.game_name.toLowerCase().includes('ark') || server.game_name.toLowerCase().includes('survival')) ? [{
            key: 'ark-config',
            label: 'ARK配置文件',
            children: <ARKConfigManager serverId={serverId!} />
          }] : []),
          ...(server && server.supports_mods && server.game_name ? [{
            key: 'mods',
            label: 'MOD管理',
            children: <ModManagement serverId={serverId!} gameType={server.game_name} />
          }] : []),
          ...(server && server.game_name && (server.game_name.toLowerCase().includes('ark') || server.game_name.toLowerCase().includes('survival')) && serverId ? [{
            key: 'shop',
            label: '商店管理',
            children: <ShopManagement serverId={serverId} />
          }] : []),
          ...(server && server.game_name && (server.game_name.toLowerCase().includes('ark') || server.game_name.toLowerCase().includes('survival')) ? [{
            key: 'plugins',
            label: '插件管理',
            children: <PluginManagement serverId={serverId!} />
          }] : []),
          {
            key: 'saves',
            label: '存档管理',
            children: <SaveManagement serverId={parseInt(serverId!)} gameType={server.game_name} />
          },
          {
            key: 'logs',
            label: '操作日志',
            children: (
              <Card>
                <Table
                  columns={logColumns}
                  dataSource={logs}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            )
          }
        ]}
      />
      </div>

      {/* 淘宝收货码验证模态框 */}
      <Modal
        title="淘宝收货码验证"
        open={receiptCodeModalVisible}
        onCancel={() => {
          setReceiptCodeModalVisible(false);
          setReceiptCodeInput('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setReceiptCodeModalVisible(false);
            setReceiptCodeInput('');
          }}>
            取消
          </Button>,
          <Button 
            key="verify" 
            type="primary" 
            loading={verifyingCode}
            onClick={() => verifyReceiptCode(receiptCodeInput)}
            disabled={!receiptCodeInput.trim()}
          >
            验证
          </Button>
        ]}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
              请输入淘宝收货码
            </h3>
            <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
              请在淘宝确认收货后，输入收到的发货码来启用服务器的完整功能
            </p>
          </div>
          
          <Form.Item label="收货码" required>
            <Input
              placeholder="请输入淘宝收货码"
              value={receiptCodeInput}
              onChange={(e) => setReceiptCodeInput(e.target.value)}
              onPressEnter={() => verifyReceiptCode(receiptCodeInput)}
              style={{ height: '40px', fontSize: '16px' }}
            />
          </Form.Item>
          
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f6ffed', 
            borderRadius: '6px', 
            border: '1px solid #b7eb8f',
            marginTop: '16px'
          }}>
            <p style={{ fontSize: '12px', color: '#52c41a', margin: 0 }}>
              💡 提示：收货码通常在淘宝确认收货后24小时内发送到您的淘宝消息中
            </p>
          </div>
        </div>
      </Modal>

      {/* 好评赠时长弹窗 */}
      <Modal
        title="好评赠时长"
        open={goodReviewModalVisible}
        onCancel={() => setGoodReviewModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setGoodReviewModalVisible(false)}>
            我知道了
          </Button>
        ]}
        width={500}
        centered
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#1890ff' }}>
            恭喜您！收货码验证成功！
          </div>
          <div style={{ fontSize: '16px', marginBottom: '20px', color: '#666' }}>
            为了感谢您的支持，我们为您准备了时长奖励：
          </div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f6ffed', 
            borderRadius: '8px', 
            border: '2px solid #52c41a',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a', marginBottom: '8px' }}>
              +{bonusDays} 天
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              服务器时长已自动延长
            </div>
          </div>
          <div style={{ fontSize: '14px', color: '#999', lineHeight: '1.5', marginBottom: '16px' }}>
            感谢您选择我们的服务！<br/>
            如有任何问题，请随时联系客服。
          </div>
          
          {reviewBonusDays > 0 && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fff7e6', 
              borderRadius: '6px', 
              border: '1px solid #ffd591',
              marginTop: '16px'
            }}>
              <div style={{ fontSize: '14px', color: '#d46b08', fontWeight: 'bold', marginBottom: '4px' }}>
                💝 好评奖励
              </div>
              <div style={{ fontSize: '16px', color: '#52c41a', fontWeight: 'bold' }}>
                好评后您还可以<span style={{ fontSize: '20px', color: '#52c41a', fontWeight: 'bold' }}>+{reviewBonusDays}天</span>！
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* 安装进度模态框 */}
      <Modal
        title="服务器安装进度"
        open={installProgressModalVisible}
        onCancel={() => setInstallProgressModalVisible(false)}
        footer={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={fetchInstallProgress}>
            刷新
          </Button>,
          ...(installProgress && installProgress.status !== 'completed' && installProgress.status !== 'error' ? [
            <Button key="cancel" danger onClick={cancelInstall}>
              取消安装
            </Button>
          ] : []),
          <Button key="close" onClick={() => setInstallProgressModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {installProgress ? (
          <div>
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">安装状态:</span>
                <Tag color={installProgress.status === 'completed' ? 'green' : 'blue'}>
                  {installProgress.status === 'completed' ? '已完成' : 
                   installProgress.status === 'downloading' ? '下载中' : 
                   installProgress.status === 'error' ? '错误' : '进行中'}
                </Tag>
              </div>
              <Progress 
                percent={installProgress.progress || 0} 
                status={installProgress.status === 'error' ? 'exception' : 'active'}
              />
            </div>
            
            <div className="mb-4">
              <div className="font-medium mb-2">当前消息:</div>
              <div className="bg-gray-50 p-3 rounded text-sm">
                {installProgress.message || '暂无消息'}
              </div>
            </div>
            
            {installProgress.logs && installProgress.logs.length > 0 && (
              <div>
                <div className="font-medium mb-2">安装日志:</div>
                <div className="bg-gray-900 text-green-400 p-3 rounded text-sm font-mono max-h-40 overflow-y-auto">
                  {installProgress.logs.map((log: string, index: number) => (
                    <div key={index} className="mb-1">
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div className="text-gray-500">正在加载安装进度...</div>
          </div>
        )}
      </Modal>

      {/* 启动进度弹窗 */}
      <Modal
        title="服务器启动进度"
        open={startupVisible}
        closable={true}
        onCancel={() => setStartupVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStartupVisible(false)}>
            关闭
          </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Progress
            type="circle"
            percent={startupProgress}
            status={startupStatus === 'error' ? 'exception' : startupStatus === 'completed' ? 'success' : 'active'}
            format={(percent) => `${percent}%`}
            size={120}
          />
          {memoryUsage > 0 && startupStatus === 'monitoring' && (
            <div style={{ marginTop: 20, fontSize: 16, color: '#666' }}>
              内存使用: {memoryUsage}MB
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 14, color: '#999' }}>
            {startupMessage}
          </div>
        </div>
      </Modal>

      {/* 分享模态框 */}
      <Modal
        title="分享游戏服务器"
        open={shareModalVisible}
        onCancel={() => setShareModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShareModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
              {server?.name}
            </h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              {server?.game_name} 服务器
            </p>
            <div style={{ 
              display: 'inline-block',
              padding: '10px 20px',
              backgroundColor: '#f0f2f5',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#1890ff',
              marginBottom: '10px'
            }}>
              {server?.node_host}:{server?.port}
            </div>
            
            {/* 在线人数显示 */}
            {server?.status === 'running' && (
              <div style={{ 
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: shareOnlineCount !== null && shareOnlineCount > 0 ? '#f6ffed' : '#fff7e6',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: shareOnlineCount !== null && shareOnlineCount > 0 ? '#52c41a' : '#faad14',
                border: `1px solid ${shareOnlineCount !== null && shareOnlineCount > 0 ? '#b7eb8f' : '#ffd591'}`,
                marginLeft: '10px'
              }}>
                {shareOnlineCountLoading ? (
                  '🔄 查询中...'
                ) : (
                  `👥 在线: ${shareOnlineCount !== null ? `${shareOnlineCount}/${server?.max_players}` : '--'}`
                )}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '15px' }}>
              快速加入游戏
            </h4>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                const steamLink = getSteamConnectLink();
                if (steamLink) {
                  window.open(steamLink, '_blank');
                }
              }}
              style={{
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                height: '50px',
                minWidth: '200px',
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              🎮 通过Steam加入游戏
            </Button>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '15px' }}>
              分享给朋友
            </h4>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Button
                type="default"
                size="large"
                onClick={copyShareLink}
                style={{
                  height: '40px',
                  minWidth: '144px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  borderColor: '#13c2c2',
                  color: '#13c2c2',
                  boxShadow: '0 4px 12px rgba(19, 194, 194, 0.2)',
                  transition: 'all 0.3s ease'
                }}
              >
                📋 复制分享链接
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  const shareUrl = `${window.location.origin}/share/server/${server.id}`;
                  window.open(shareUrl, '_blank');
                }}
                style={{
                  height: '40px',
                  minWidth: '144px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  backgroundColor: '#722ed1',
                  borderColor: '#722ed1',
                  boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                👁️ 查看页面详情
              </Button>
            </div>
          </div>

          <div style={{ 
            padding: '15px',
            backgroundColor: '#f6ffed',
            borderRadius: '8px',
            border: '1px solid #b7eb8f'
          }}>
            <p style={{ 
              fontSize: '14px', 
              color: '#52c41a',
              margin: 0,
              fontWeight: '500'
            }}>
              💡 提示：复制分享链接可以分享给朋友，查看页面详情可以预览分享页面的效果，Steam链接可以直接启动游戏并加入服务器
            </p>
          </div>
        </div>
      </Modal>

      {/* 续费模态框 */}
      <Modal
        title="服务器续费"
        open={renewModalVisible}
        onCancel={() => {
          setRenewModalVisible(false);
          setCouponCode('');
          setCouponValidation(null);
        }}
        footer={null}
        width={600}
      >
        <Form onFinish={handleRenew} layout="vertical">
          {/* 付款周期选择 */}
          <Form.Item label="选择付款周期" required>
            <div className="grid grid-cols-1 gap-3">
              {renewalInfo?.pricingOptions.map((option) => (
                <div
                  key={option.billingPeriod}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedBillingPeriod === option.billingPeriod
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setSelectedBillingPeriod(option.billingPeriod);
                    setCouponCode('');
                    setCouponValidation(null);
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">{getBillingPeriodName(option.billingPeriod)}</div>
                      <div className="text-sm text-gray-500">{option.durationDays}天</div>
                    </div>
                    <div className="text-lg font-bold text-blue-600">¥{option.price}</div>
                  </div>
                </div>
              ))}
            </div>
          </Form.Item>

          {/* 优惠码输入 */}
          <Form.Item label="优惠码（可选）">
            <div className="flex space-x-2">
              <Input
                placeholder="输入优惠码"
                value={couponCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCouponCode(e.target.value);
                  // 清空之前的验证结果
                  if (!e.target.value) {
                    setCouponValidation(null);
                  }
                }}
                className="flex-1"
              />
              <Button
                type="default"
                onClick={() => validateCoupon(couponCode)}
                loading={couponLoading}
                disabled={!couponCode || !selectedBillingPeriod}
              >
                验证
              </Button>
            </div>
            {couponValidation && (
              <div className={`mt-2 p-2 rounded text-sm ${
                couponValidation.error 
                  ? 'bg-red-50 text-red-600 border border-red-200' 
                  : 'bg-green-50 text-green-600 border border-green-200'
              }`}>
                {couponValidation.error || `优惠码有效，减免 ¥${couponValidation.discount_value}${couponValidation.discount_type === 'percentage' ? ` (${couponValidation.discount_value}%)` : ''}`}
              </div>
            )}
          </Form.Item>
          
          {/* 价格显示 */}
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">原价：</span>
              <span className="text-lg">¥{getCurrentPrice() || 0}</span>
            </div>
            {couponValidation && !couponValidation.error && (
              <div className="flex justify-between items-center mb-2 text-green-600">
                <span>优惠减免：</span>
                <span>-¥{((getCurrentPrice() || 0) - (getFinalPrice() || 0)).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center text-lg font-bold border-t pt-2">
              <span>实付金额：</span>
              <span className="text-blue-600">¥{(getFinalPrice() || 0).toFixed(2)}</span>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              续费将从当前到期时间开始计算
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button onClick={() => {
              setRenewModalVisible(false);
              setCouponCode('');
              setCouponValidation(null);
            }}>
              取消
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              disabled={!selectedBillingPeriod}
            >
              确认续费
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ServerDetail;



